import { gql } from 'apollo-boost';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Query } from 'react-apollo';
import { FaMobileAlt } from 'react-icons/fa';
import {
  Button,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import styled from 'styled-components';
import { showConfirmation } from '../ConfirmationDialogService';
import {
  displayError,
  displaySuccess,
  getHospitalSiteCode,
  logAmplitudeEvent
} from '../utilities';
import { passthroughFetch } from '../utilities/passthroughFetch';
import DiscountInput, { discountTypes } from './DiscountInput';
import Loader from './Loader';
import PaymentForm from './PaymentForm';
import Veil from './Veil';
import SelectClinixProcedure from './SelectClinixProcedure';
import { fetchCustomerDetails } from '../utilities/fetchCustomerDetails';

const DivTopMargin = styled.div`
  margin-top: 10px;
`;

const DoctorSpecialization = styled.span`
  color: '#808080',
  textTransform: 'capitalize'
`;

const DOC_QUERY = gql`
  query doctor($id: String) {
    doctor: getDoctor(id: $id) {
      id
      name
      qualification
      lifetrenzId
      hospitals: getHospitals {
        id
        name
      }
    }
  }
`;

const DISPLAY_BOOKING_QUERY = gql`
  query DISPLAY_BOOKING_QUERY($bookingId: String) {
    booking: getBooking(id: $bookingId) {
      id
      mpid
      prospectId
      hospitalId
      userName
      userMobile
      userEmail
      slotTime
      departmentId
    }
  }
`;

const CreateInvoiceModal = props => {
  const {
    isOpen,
    toggle,
    doctorId,
    bookingId,
    selectedHospitalDetails
  } = props;
  const [selectedProcedure, updateSelectedProcedure] = useState();
  const [shouldShowChargesLoader, showChargesLoader] = useState(false);
  const [bookingData, updateBookingData] = useState(undefined);
  const [doctorData, updateDoctorData] = useState(undefined);
  const [totalAmount, updateTotalAmount] = useState(undefined);
  const [chargesErrorMsg, updateChargesErrorMsg] = useState('');
  const [paymentData, updatePaymentData] = useState({});
  const [procedureList, updateProcedureList] = useState('');
  const [customerDetails, setCustomerDetails] = useState({});
  const [consultChargeloader, updateConsultChargeloader] = useState(false);
  const [procedureCompleteDetails, updateProcedureCompleteDetails] = useState(
    ''
  );
  const [selectedProcedureDetails, updateSelectedProcedureDetails] = useState();
  const [shouldShowCreateInvoiceLoader, showCreateInvoiceLoader] = useState(
    false
  );
  const [invoiceGenerationSuccess, updateInvoiceGenerationSuccess] = useState(
    false
  );
  const [pdfLink, updatePdfLink] = useState(null);

  const [discount, updateDiscount] = useState(undefined);
  const [discountType, setDiscountType] = useState(
    discountTypes.find(item => item.value === 'percentage')
  );
  const [discountNotes, setDiscountNotes] = useState('');

  useEffect(() => {
    updateSelectedProcedure();
    showChargesLoader(false);
    updateBookingData(undefined);
    updateDoctorData(undefined);
    updateTotalAmount(undefined);
    updateDiscount(undefined);
    updateChargesErrorMsg('');
    updatePaymentData({});
    updateProcedureList('');
    setCustomerDetails({});
    updateConsultChargeloader(false);
    updateProcedureCompleteDetails('');
    updateSelectedProcedureDetails();
    showCreateInvoiceLoader(false);
    updateInvoiceGenerationSuccess(false);
    updatePdfLink(null);
    if (isOpen && selectedHospitalDetails.chargeMaster === 'zoho_books') {
      getDocChargesZohoBooks(doctorId, 'Consult');
    }
    // eslint-disable-next-line
  }, [isOpen]);

  const getCustomerDetails = async userMPID => {
    try {
      const response = await fetchCustomerDetails(userMPID);
      if (response) {
        setCustomerDetails(response);
      } else {
        setCustomerDetails({});
      }
    } catch (error) {
      setCustomerDetails({});
    }
  };

  // function to fetch doctor charges configured in zoho books based on amsdoctorid and charge item type.
  const fetchDocCharges = (doctorId, chargeType) => {
    const req_body = {
      amsdoctorid: doctorId,
      cf_chargeitemtype: chargeType,
      cf_hospitalid: selectedHospitalDetails.id,
      siteCode: selectedHospitalDetails.siteCode
    };

    return passthroughFetch({
      url: '/clinix/v2/get_charges',
      app: 'cxServer',
      method: 'POST',
      payload: req_body
    });
  };

  const getDocChargesZohoBooks = async (doctorId, chargeType) => {
    try {
      updateConsultChargeloader(true);
      const response = await fetchDocCharges(doctorId, chargeType);
      if (response && response.status && response.status === 1) {
        updateProcedureCompleteDetails(response.data);
        let procedureList = response.data.map(p => p.item_name);
        updateProcedureList(procedureList);
        logAmplitudeEvent(
          'doc_charge_fetched_successfully',
          {
            doctorId: doctorId,
            chargeType: chargeType
          },
          true
        );
        updateConsultChargeloader(false);
      } else {
        logAmplitudeEvent(
          'doc_charge_fetching_failed',
          {
            doctorId: doctorId,
            chargeType: chargeType,
            response: response
          },
          true
        );
        updateConsultChargeloader(false);
      }
    } catch (error) {
      logAmplitudeEvent(
        'doc_charge_fetching_failed',
        {
          doctorId: doctorId,
          chargeType: chargeType,
          error: error
        },
        true
      );
      updateConsultChargeloader(false);
    }
  };

  // function to calculate patient age in years and months based on his date of birth.
  const calculateAge = dateString => {
    let today = new Date();
    let DOB = new Date(dateString);
    let totalMonths =
      (today.getFullYear() - DOB.getFullYear()) * 12 +
      today.getMonth() -
      DOB.getMonth();
    totalMonths += today.getDay() < DOB.getDay() ? -1 : 0;
    let years = today.getFullYear() - DOB.getFullYear();

    if (DOB.getMonth() > today.getMonth()) years = years - 1;
    else if (DOB.getMonth() === today.getMonth())
      if (DOB.getDate() > today.getDate()) years = years - 1;

    let months;

    if (DOB.getDate() > today.getDate()) {
      months = totalMonths % 12;
      if (months === 0) months = 11;
    } else {
      if (DOB.getMonth() === today.getMonth()) months = totalMonths % 12;
      else months = (totalMonths % 12) + 1;
    }
    let age = years + ' years ' + months + ' months ';
    return age;
  };

  // function to create customer’s payment invoice based on get_charges API response and other customer payment details like ("payment_mode").
  const createInvoiceInUsersServiceAndZohoBooks = (
    siteCode,
    patientAge,
    patientGender
  ) => {
    let loggedInUser = JSON.parse(localStorage.getItem('user'));

    const req_body = {
      appointmentId: bookingData.id,
      mpid: bookingData.mpid,
      salesperson_name: loggedInUser.name,
      reference_number: paymentData.transactionId
        ? paymentData.transactionId
        : 'N/A',
      txnid: paymentData.transactionId ? paymentData.transactionId : 'N/A',
      payment_mode: paymentData.paymentMode,
      gst_no: selectedHospitalDetails.gstNo,
      first_name: bookingData.userName,
      email: bookingData.userEmail,
      mobile: bookingData.userMobile,
      product_type: selectedProcedureDetails[0].product_type,
      sitecode: siteCode,
      hospital_id: selectedHospitalDetails.id.toString(),
      hospital_name: selectedHospitalDetails.name,
      hospital_address: selectedHospitalDetails.address,
      hospital_email: selectedHospitalDetails.appSupportEmail,
      line_items: selectedProcedureDetails,
      procedure: 'Consultation',
      logo_url: selectedHospitalDetails.logoUrl,
      ams_doctor_id: doctorData.id,
      doctor_name: doctorData.name,
      notes: {
        patient_age: patientAge,
        patient_gender: patientGender,
        selected_hospital_name: selectedHospitalDetails.name,
        ...(!selectedHospitalDetails.isCloudnineHosp
          ? { selected_clinic_address: selectedHospitalDetails.address }
          : { selected_hospital_address: selectedHospitalDetails.address })
      },
      discount: discount ? parseInt(discount) : 0,
      discountType: discountType.value
    };

    if (req_body.discount && discountNotes) {
      req_body.discountNotes = discountNotes;
    }

    return passthroughFetch({
      url: '/clinix/v2/create_invoice',
      app: 'cxServer',
      method: 'POST',
      payload: req_body
    });
  };

  const createInvoiceInUsersService = (siteCode, patientAge, patientGender) => {
    let loggedInUser = JSON.parse(localStorage.getItem('user'));

    const req_body = {
      mpid: bookingData.mpid,
      salesperson_name: loggedInUser.name,
      reference_number: paymentData.transactionId
        ? paymentData.transactionId
        : 'N/A',
      txnid: paymentData.transactionId ? paymentData.transactionId : 'N/A',
      payment_mode: paymentData.paymentMode,
      procedure: 'Consultation',
      first_name: bookingData.userName,
      email: bookingData.userEmail,
      mobile: bookingData.userMobile,
      appointmentId: bookingData.id,
      gst_no: selectedHospitalDetails.gstNo,
      sitecode: siteCode,
      doctor_id: doctorData.id,
      doctor_name: doctorData.name,
      hospital_id: selectedHospitalDetails.id.toString(),
      hospital_name: selectedHospitalDetails.name,
      hospital_email: selectedHospitalDetails.appSupportEmail,
      logo_url: selectedHospitalDetails.logoUrl,
      notes: {
        patient_age: patientAge,
        patient_gender: patientGender,
        ...(selectedHospitalDetails.isCloudnineHosp
          ? { selected_hospital_address: selectedHospitalDetails.address }
          : { selected_clinic_address: selectedHospitalDetails.address })
      },
      discount: discount ? parseInt(discount) : 0,
      discountType: discountType.value,
      line_items: selectedProcedureDetails
    };

    if (req_body.discount && discountNotes) {
      req_body.discountNotes = discountNotes;
    }

    setDiscountNotes('');
    return passthroughFetch({
      url: '/clinix/v2/create_consult_invoice',
      app: 'cxServer',
      method: 'POST',
      payload: req_body
    });
  };

  const createPaymentInvoice = async () => {
    let patientAge = calculateAge(customerDetails.date_of_birth);
    let loggedInUser = JSON.parse(localStorage.getItem('user'));
    const sitecodeResp = await getSiteCodeofId(bookingData.hospitalId);
    const siteCode = sitecodeResp[0].siteCode;
    try {
      let response = {};
      if (
        selectedHospitalDetails.settings &&
        selectedHospitalDetails.settings.chargeMaster &&
        selectedHospitalDetails.settings.chargeMaster
          .invoiceGeneratorForConsultation === 'users_service_and_zoho_books'
      ) {
        response = await createInvoiceInUsersServiceAndZohoBooks(
          siteCode,
          patientAge,
          customerDetails.gender
        );
      } else {
        response = await createInvoiceInUsersService(
          siteCode,
          patientAge,
          customerDetails.gender
        );
      }

      if (response && response.status && response.status === 1) {
        showCreateInvoiceLoader(false);
        updateInvoiceGenerationSuccess(true);
        displaySuccess('Invoice generated successfully');
        updatePdfLink(response.data.s3pdflink);

        logAmplitudeEvent(
          'Invoice_created_successfully',
          {
            mpid: bookingData.mpid,
            salespersonName: loggedInUser.name,
            referenceNumber: paymentData.transactionId,
            txnId: paymentData.transactionId,
            paymentMode: paymentData.paymentMode,
            itemId: selectedProcedureDetails.item_id,
            procedure: selectedProcedureDetails.cf_procedure,
            taxExemptionId: selectedProcedureDetails.tax_exemption_id,
            description: selectedProcedureDetails.description,
            gstNo: selectedHospitalDetails.gstNo,
            userName: bookingData.userName,
            email: bookingData.userEmail,
            mobile: bookingData.userMobile,
            appointmentId: bookingData.id,
            product_type: selectedProcedureDetails.product_type,
            hsn_or_sac: selectedProcedureDetails.hsn_or_sac,
            rate: selectedProcedureDetails.rate,
            sitecode: siteCode,
            doctor_id: doctorData.id,
            hospital_id: selectedHospitalDetails.id.toString(),
            hospital_name: selectedHospitalDetails.name,
            doctor_name: doctorData.name,
            hospital_address: selectedHospitalDetails.address,
            hospital_email: selectedHospitalDetails.appSupportEmail,
            s3pdflink: response.s3pdflink
          },
          true
        );
      } else {
        logAmplitudeEvent(
          'Invoice_creation_failed',
          {
            mpid: bookingData.mpid,
            salespersonName: loggedInUser.name,
            referenceNumber: paymentData.transactionId,
            txnId: paymentData.transactionId,
            paymentMode: paymentData.paymentMode,
            itemId: selectedProcedureDetails.item_id,
            procedure: selectedProcedureDetails.cf_procedure,
            taxExemptionId: selectedProcedureDetails.tax_exemption_id,
            description: selectedProcedureDetails.description,
            gstNo: selectedHospitalDetails.gstNo,
            userName: bookingData.userName,
            email: bookingData.userEmail,
            mobile: bookingData.userMobile,
            appointmentId: bookingData.id,
            product_type: selectedProcedureDetails.product_type,
            hsn_or_sac: selectedProcedureDetails.hsn_or_sac,
            rate: selectedProcedureDetails.rate,
            sitecode: siteCode,
            doctor_id: doctorData.id,
            doctor_name: doctorData.name,
            hospital_id: selectedHospitalDetails.id.toString(),
            hospital_name: selectedHospitalDetails.name,
            hospital_address: selectedHospitalDetails.address,
            hospital_email: selectedHospitalDetails.appSupportEmail,
            response: response
          },
          true
        );
        showCreateInvoiceLoader(false);
        const emsg = `Invoice generation failed: ${response}`;
        throw new Error(emsg);
      }
    } catch (error) {
      logAmplitudeEvent(
        'Invoice_creation_failed',
        {
          mpid: bookingData.mpid,
          salespersonName: loggedInUser.name,
          referenceNumber: paymentData.transactionId,
          txnId: paymentData.transactionId,
          paymentMode: paymentData.paymentMode,
          itemId: selectedProcedureDetails.item_id,
          procedure: selectedProcedureDetails.cf_procedure,
          taxExemptionId: selectedProcedureDetails.tax_exemption_id,
          description: selectedProcedureDetails.description,
          gstNo: selectedHospitalDetails.gstNo,
          userName: bookingData.userName,
          email: bookingData.userEmail,
          mobile: bookingData.userMobile,
          appointmentId: bookingData.id,
          product_type: selectedProcedureDetails.product_type,
          hsn_or_sac: selectedProcedureDetails.hsn_or_sac,
          rate: selectedProcedureDetails.rate,
          sitecode: siteCode,
          doctor_id: doctorData.id,
          doctor_name: doctorData.name,
          hospital_id: selectedHospitalDetails.id.toString(),
          hospital_name: selectedHospitalDetails.name,
          hospital_address: selectedHospitalDetails.address,
          hospital_email: selectedHospitalDetails.appSupportEmail,
          error: error
        },
        true
      );
      showCreateInvoiceLoader(false);
      displayError(error);
    }
  };

  const handleOnClose = () => {
    updateSelectedProcedure();
    updateBookingData(undefined);
    updateDoctorData(undefined);
    showChargesLoader(false);
    updateTotalAmount(undefined);
    updateDiscount(undefined);
    updateChargesErrorMsg('');
    updatePaymentData({});
    showCreateInvoiceLoader(false);
    updateInvoiceGenerationSuccess(false);
    updatePdfLink(null);
  };

  const getSiteCodeofId = async id => {
    const getData = await getHospitalSiteCode();
    const hospData = getData.data.getHospitals;

    return hospData.filter(opt => opt.id === id);
  };

  if (!doctorId || !bookingId) {
    return null;
  }

  const updatePaymentDetails = (key, value) => {
    const updatedPayData = { ...paymentData, [key]: value };
    if (key === 'cardNumber' && /^[0-9]+$/.test(value))
      updatedPayData[key] = parseInt(value);
    updatePaymentData(updatedPayData);
  };

  const createInvoice = async () => {
    showCreateInvoiceLoader(true);
    updateInvoiceGenerationSuccess(false);
    if (selectedHospitalDetails.chargeMaster === 'zoho_books') {
      createPaymentInvoice();
    }
  };

  const viewInvoice = () => {
    const url = pdfLink;
    window.open(url, '_blank');
  };

  const calcFinalAmount = () => {
    if (!discount || discount === '0') {
      return totalAmount;
    }

    if (discountType.value === 'percentage') {
      return (totalAmount - totalAmount * (parseInt(discount) / 100)).toFixed(
        2
      );
    } else {
      return totalAmount - parseInt(discount);
    }
  };

  const calcDiscountedAmount = () => {
    if (discountType.value === 'percentage') {
      return (totalAmount * (parseInt(discount) / 100)).toFixed(2);
    } else {
      return discount;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      onClosed={handleOnClose}
      keyboard={false}
      backdrop="static"
    >
      <ModalHeader toggle={toggle}>Create Invoice</ModalHeader>
      <ModalBody>
        <Container>
          <Query
            query={DISPLAY_BOOKING_QUERY}
            variables={{ bookingId }}
            onCompleted={data => {
              getCustomerDetails(data.booking.mpid);
              updateBookingData(data.booking);
            }}
          >
            {({ loading, data }) => {
              if (loading) return <Loader />;

              const {
                booking: { userName, userMobile, mpid, slotTime, prospectId }
              } = data;

              return (
                <Row>
                  <Col sm={9}>
                    <h2>{userName}</h2>
                    <div>
                      <span className="text-muted">
                        MPI: {mpid ? mpid : 'Not Available'}{' '}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted">
                        Prospect ID: {prospectId}
                      </span>
                    </div>
                    <div>
                      <span
                        className="appt-time"
                        style={{ fontWeight: 'bold' }}
                      >
                        Appointment Time:{' '}
                        {`${moment(slotTime).format(
                          'ddd DD MMM YYYY hh:mm A'
                        )}`}
                      </span>
                    </div>
                    <hr />
                  </Col>
                  <Col sm={3} className="text-right">
                    <FaMobileAlt />
                    <span>{` ${userMobile}`}</span>
                  </Col>
                </Row>
              );
            }}
          </Query>
          <Query
            query={DOC_QUERY}
            variables={{ id: doctorId }}
            onCompleted={data => updateDoctorData(data.doctor)}
          >
            {({ loading, data: { doctor } = { doctor: {} } }) => {
              if (loading) {
                return <Loader loading={true} />;
              }

              const { name, qualification } = doctor;

              return (
                <Row>
                  <Col>
                    <Row>
                      <Col>
                        <h4 style={{ display: 'inline' }}>{name}</h4>{' '}
                        <DoctorSpecialization>
                          {qualification}
                        </DoctorSpecialization>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: '1rem' }}>
                      <Col sm={6}>
                        {bookingData ? (
                          consultChargeloader ? (
                            <Loader />
                          ) : (
                            <SelectClinixProcedure
                              procedureList={procedureList}
                              procedureType={'Consultation'}
                              selectedProcedure={selectedProcedure}
                              enteredSearchText={() => {}}
                              onProcedureSelect={selectedProcedure => {
                                let selectedProcedureArr = [];
                                let selectedProcedureCompleteArr = [];

                                procedureCompleteDetails.forEach(
                                  procedureDetails => {
                                    selectedProcedure.forEach(procedure => {
                                      if (
                                        procedureDetails.item_name ===
                                        procedure.value
                                      ) {
                                        selectedProcedureCompleteArr.push(
                                          procedureDetails
                                        );
                                        selectedProcedureArr.push({
                                          item_id: procedureDetails.item_id,
                                          header_name: `${procedureDetails.item_name} invoice`,
                                          is_taxable: false,
                                          tax_exemption_id:
                                            procedureDetails.tax_exemption_id,
                                          name: procedureDetails.name,
                                          description:
                                            procedureDetails.description,
                                          hsn_or_sac:
                                            procedureDetails.hsn_or_sac,
                                          product_type:
                                            procedureDetails.product_type,
                                          rate: procedureDetails.rate,
                                          item_total: procedureDetails.rate
                                        });
                                      }
                                    });
                                  }
                                );

                                let totalProcedureAmount = 0;

                                selectedProcedureArr = selectedProcedureArr.filter(
                                  (arr, index, self) =>
                                    index ===
                                    self.findIndex(
                                      t => t.item_id === arr.item_id
                                    )
                                );

                                selectedProcedureCompleteArr = selectedProcedureCompleteArr.filter(
                                  (arr, index, self) =>
                                    index ===
                                    self.findIndex(
                                      t => t.item_id === arr.item_id
                                    )
                                );

                                selectedProcedureCompleteArr.forEach(
                                  procedure => {
                                    totalProcedureAmount =
                                      totalProcedureAmount + procedure.rate;
                                  }
                                );

                                if (totalProcedureAmount > 0) {
                                  showChargesLoader(false);
                                  updateChargesErrorMsg('');
                                  updateTotalAmount(totalProcedureAmount);
                                  updateSelectedProcedureDetails(
                                    selectedProcedureArr
                                  );
                                } else {
                                  showChargesLoader(false);
                                  updateChargesErrorMsg(
                                    'Some error occured. Try again later'
                                  );
                                  updateSelectedProcedureDetails();
                                  updateTotalAmount();
                                }
                              }}
                            />
                          )
                        ) : null}
                      </Col>
                      <Col sm={6}>
                        {shouldShowChargesLoader ? (
                          <Loader loading={true} />
                        ) : null}
                        {chargesErrorMsg ? (
                          <span className="error">{chargesErrorMsg}</span>
                        ) : null}
                        {discount && discount !== '0' ? (
                          <h6 style={{ fontWeight: 'bold' }}>
                            Discount Applied: &#x20B9; {calcDiscountedAmount()}
                          </h6>
                        ) : null}
                        {totalAmount ? (
                          <h6 style={{ fontWeight: 'bold' }}>
                            Total Amount: &#x20B9; {calcFinalAmount()}
                          </h6>
                        ) : null}
                      </Col>
                    </Row>
                    {selectedProcedureDetails ? (
                      <Row className="form-row mt-2">
                        <Col>
                          <DiscountInput
                            discount={discount || ''}
                            setDiscount={updateDiscount}
                            discountType={discountType}
                            setDiscountType={setDiscountType}
                            discountNotes={discountNotes}
                            setDiscountNotes={setDiscountNotes}
                            totalAmount={totalAmount}
                          />
                        </Col>
                      </Row>
                    ) : null}
                  </Col>
                </Row>
              );
            }}
          </Query>
          <Row>
            <Col>
              <DivTopMargin className="card">
                <CardHeader>{'Enter Payment Details'}</CardHeader>
                <Collapse
                  isOpen={
                    totalAmount && parseFloat(totalAmount) >= 0 ? true : false
                  }
                >
                  <CardBody>
                    <fieldset
                      disabled={
                        shouldShowCreateInvoiceLoader ||
                        invoiceGenerationSuccess
                      }
                    >
                      {shouldShowCreateInvoiceLoader && <Veil />}
                      <PaymentForm
                        paymentData={paymentData}
                        onChange={updatePaymentDetails}
                        onSubmit={() => {
                          if (
                            (!paymentData.transactionId ||
                              paymentData.transactionId.length < 5) &&
                            paymentData.paymentMode !== 'Cash'
                          ) {
                            displayError(
                              'Please provide a valid transaction id, minimum 5 digits !!!'
                            );
                            return;
                          } else {
                            showConfirmation(
                              'Create Invoice Confirmation',
                              'Are you sure you want to create invoice for this appointment?',
                              () => createInvoice()
                            );
                          }
                        }}
                      />
                    </fieldset>
                  </CardBody>
                </Collapse>
              </DivTopMargin>
            </Col>
          </Row>
        </Container>
      </ModalBody>
      <ModalFooter>
        {pdfLink ? (
          <Button color="success" onClick={viewInvoice}>
            View Invoice
          </Button>
        ) : null}
        <Button color="secondary" onClick={toggle}>
          CLOSE
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CreateInvoiceModal.propTypes = {
  doctorId: PropTypes.string,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  bookingId: PropTypes.string,
  selectedHospitalDetails: PropTypes.object
};

export default CreateInvoiceModal;
